import { useCallback, useEffect, useState } from 'react'
import { ApiError } from '@services/apiClient'
import { getMe, updateMe, type MeProfileWire } from '../api/accountApi'
import { accountProfileStore } from '../services/accountProfileStore'

/**
 * T-G7: Account profile hook (`pages/settings/AccountSection.tsx` 用)。
 *
 * - mount で GET /me
 * - save() で PATCH /me
 * - エラーは messageKey に変換 (CLAUDE.md §2 ルール 10)
 */

export type AccountState =
  | { kind: 'loading' }
  | { kind: 'ready'; profile: MeProfileWire }
  | { kind: 'error'; messageKey: string }

const errorKey = (err: unknown): string => {
  if (err instanceof ApiError) {
    if (err.status === 401) return 'settings.account.error.no_session'
    if (err.status === 404) return 'settings.account.error.not_found'
    if (err.status === 429) return 'settings.account.error.too_many_requests'
    if (err.status === 400) return 'settings.account.error.bad_request'

    return 'settings.account.error.network'
  }

  return 'settings.account.error.generic'
}

export interface UseAccountProfile {
  state: AccountState
  lastErrorKey: string | null
  saving: boolean
  saved: boolean
  save: (name: string) => Promise<void>
  refresh: () => Promise<void>
}

export const useAccountProfile = (): UseAccountProfile => {
  const [state, setState] = useState<AccountState>({ kind: 'loading' })
  const [lastErrorKey, setLastErrorKey] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  const refresh = useCallback(async (): Promise<void> => {
    setLastErrorKey(null)
    try {
      const profile = await getMe()

      setState({ kind: 'ready', profile })
      accountProfileStore.updateName(profile.name)
    } catch (err: unknown) {
      const key = errorKey(err)

      setState({ kind: 'error', messageKey: key })
      setLastErrorKey(key)
    }
  }, [])

  const save = useCallback(async (name: string): Promise<void> => {
    setLastErrorKey(null)
    setSaved(false)
    setSaving(true)
    try {
      const profile = await updateMe({ name })

      setState({ kind: 'ready', profile })
      accountProfileStore.updateName(profile.name)
      setSaved(true)
      window.setTimeout(() => setSaved(false), 3000)
    } catch (err: unknown) {
      setLastErrorKey(errorKey(err))
    } finally {
      setSaving(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  return { state, lastErrorKey, saving, saved, save, refresh }
}
